import {
  INotification,
  notificationEnums
} from '@otw/models'
import { inventoryV3Href } from '@/views/SuperAdmin/InventoryV3/inventoryV3Route'

/**
 * Determines the destination URL when a notification is clicked
 * @param notification The notification object
 * @returns The URL to navigate to, or undefined if no navigation should occur
 */
export const handleNotificationClick = (notification: INotification): string | undefined => {
  const { type, afterData, beforeData } = notification

  if (Object.values(notificationEnums.AdminNotificationType).includes(type as any)) {
    switch (type) {
      case notificationEnums.AdminNotificationType.ADMIN_USER_SET_INITIAL_PASSWORD:
      case notificationEnums.AdminNotificationType.ADMIN_USER_CREATED_EVENT:
        if (afterData && 'id' in afterData) {
          return `/admin/users/${afterData.id}`;
        }
        return "/admin/users";

      case notificationEnums.AdminNotificationType.ADMIN_INVENTORY_VEHICLE_ADDED:
      case notificationEnums.AdminNotificationType.ADMIN_INVENTORY_VEHICLE_DELETED:
      case notificationEnums.AdminNotificationType.ADMIN_INVENTORY_PUBLISHED_ON_SITE_EVENT:
      case notificationEnums.AdminNotificationType.ADMIN_INVENTORY_SOLD_EVENT:
      case notificationEnums.AdminNotificationType.ADMIN_VEHICLE_PICKUP_LOCATION_NEEDED:
      case notificationEnums.AdminNotificationType.ADMIN_VEHICLE_PICKUP_LOCATION_COMPLETED:
      case notificationEnums.AdminNotificationType.ADMIN_VEHICLE_PENDING_SALE_NOT_PURCHASED:
      case notificationEnums.AdminNotificationType.ADMIN_VEHICLE_PAID_FOR_TITLE_REQUEST:
      case notificationEnums.AdminNotificationType.ADMIN_VEHICLE_FUNDED:
      case notificationEnums.AdminNotificationType.ADMIN_TITLE_RECEIVED:
      case notificationEnums.AdminNotificationType.ADMIN_INVENTORY_PAYOFF_EXPIRING:
      case notificationEnums.AdminNotificationType.SALES_REP_DELIVERY_COMPLETE:
      case notificationEnums.AdminNotificationType.SALES_REP_VEHICLE_FUNDED:
      case notificationEnums.AdminNotificationType.ADMIN_MANAGEMENT_DELIVERY_COMPLETE:
        if (afterData && 'id' in afterData) {
          return inventoryV3Href({ open: 'edit', truckId: String(afterData.id) });
        }
        if (type === notificationEnums.AdminNotificationType.ADMIN_INVENTORY_VEHICLE_DELETED && beforeData && 'id' in beforeData) {
          return inventoryV3Href({ open: 'edit', truckId: (beforeData as { id: string }).id });
        }
        return "/admin/inventory-v3";

      case notificationEnums.AdminNotificationType.SALES_REP_REPAIR_COMPLETE:
      case notificationEnums.AdminNotificationType.ADMIN_MANAGEMENT_REPAIR_COMPLETE: {
        const transportTruckId =
          afterData &&
          typeof afterData === 'object' &&
          'transport_truck_id' in afterData &&
          typeof (afterData as { transport_truck_id?: unknown }).transport_truck_id === 'string'
            ? (afterData as { transport_truck_id: string }).transport_truck_id
            : undefined;
        if (transportTruckId) {
          return `/admin/management/transporter-management?transportTruckId=${encodeURIComponent(transportTruckId)}`;
        }
        return "/admin/management/transporter-management";
      }

      case notificationEnums.AdminNotificationType.ADMIN_APPLICATION_CREATED_EVENT:
      case notificationEnums.AdminNotificationType.ADMIN_APPLICATION_STATUS_CHANGE_EVENT:
        if (afterData && 'id' in afterData) {
          return `/admin/applications/${afterData.id}`;
        }
        return "/admin/applications";

      case notificationEnums.AdminNotificationType.ADMIN_LEAD_CREATED_EVENT:
      case notificationEnums.AdminNotificationType.ADMIN_LEAD_SHIPPING_INFORMATION_NEEDED:
      case notificationEnums.AdminNotificationType.ADMIN_LEAD_SHIPPING_QUOTE_COMPLETE:
        if (afterData && 'id' in afterData) {
          return `/admin/leads/${afterData.id}`;
        }
        return "/admin/leads";

      case notificationEnums.AdminNotificationType.ADMIN_OVERDUE_VEHICLE_REMINDER:
      case notificationEnums.AdminNotificationType.ADMIN_REPAIR_5_DAYS_REMINDER:
      case notificationEnums.AdminNotificationType.ADMIN_TRANSPORTER_SLA_OVERDUE_DAILY:
      case notificationEnums.AdminNotificationType.ADMIN_TRANSPORTER_DRIVER_CREATION_REQUEST:
      case notificationEnums.AdminNotificationType.ADMIN_TRANSPORTER_INSPECTION_CHECKLIST_COMPLETED:
      case notificationEnums.AdminNotificationType.ADMIN_TRANSPORTER_REPAIR_ESTIMATE_UPLOADED:
      case notificationEnums.AdminNotificationType.ADMIN_TRANSPORTER_REPAIR_TIMELINE_UPDATED:
      case notificationEnums.AdminNotificationType.ADMIN_TRANSPORTER_SHIPMENT_REJECTED:
        return "/admin/management/transporter-management";

      case notificationEnums.AdminNotificationType.ADMIN_TITLE_OVERVIEW:
      case notificationEnums.AdminNotificationType.ADMIN_TITLE_REMINDER:
      case notificationEnums.AdminNotificationType.ADMIN_SOLD_TITLE_IN_TRANSIT_REMINDER:
      case notificationEnums.AdminNotificationType.ADMIN_ELECTRONIC_TITLE_LIEN_RELEASE_REMINDER:
        return "/admin/title-manager";

      case notificationEnums.AdminNotificationType.STOCK_NUMBER_PAYOFF_EXPIRING:
      case notificationEnums.AdminNotificationType.STOCK_NUMBER_PICKUP_LOCATION_NEEDED:
        if (afterData && 'id' in afterData) {
          return inventoryV3Href({ open: 'edit', truckId: String(afterData.id) });
        }
        return "/admin/inventory-v3";

      case notificationEnums.AdminNotificationType.ADMIN_VEHICLE_GATE_PASS_NEEDED:
      case notificationEnums.AdminNotificationType.STOCK_NUMBER_GATE_PASS_NEEDED:
      case notificationEnums.AdminNotificationType.ADMIN_VEHICLE_PENDING_SALE_TITLE_COPY_NEEDED:
      case notificationEnums.AdminNotificationType.STOCK_NUMBER_PENDING_SALE_TITLE_COPY_NEEDED:
      case notificationEnums.AdminNotificationType.ADMIN_VEHICLE_FUNDED_NEEDS_SOLD:
      case notificationEnums.AdminNotificationType.ADMIN_VEHICLE_PURCHASED_NEEDS_PICKUP_LOCATION:
      case notificationEnums.AdminNotificationType.STOCK_NUMBER_PURCHASED_NEEDS_PICKUP_LOCATION:
        return "/admin/inventory-v3";

      case notificationEnums.AdminNotificationType.STOCK_NUMBER_TITLE_OVERVIEW:
      case notificationEnums.AdminNotificationType.STOCK_NUMBER_TITLE_REMINDER:
        return "/admin/title-manager";

      case notificationEnums.AdminNotificationType.ADMIN_PTO_REQUEST_CREATED:
        return "/admin/time-tracker";
    }
  }

  return undefined;
};
